import { Router } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ButtonComponent } from '../../shared/reusable/button/button.component';
import { BiometricService } from '../../services/biometric.service';
import { SpinnerService } from '../../services/spinner.service';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-biometric-verification',
  templateUrl: './biometric-verification.component.html',
  styleUrls: ['./biometric-verification.component.scss'],
  imports: [ButtonComponent, CommonModule],
  standalone: true,
})
export class BiometricVerificationComponent implements OnInit, OnDestroy {
  verificationStatus: 'pending' | 'in_progress' | 'completed' | 'failed' = 'pending';
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private biometricService: BiometricService,
    private spinner: SpinnerService,
    private dataService: DataService,
    private authService: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    const status = this.dataService.verificationStatus$();

    if (!status?.biometric_required || status?.biometric_completed) {
      this.router.navigate(['/summary']);
      return;
    }

    await this.initializeSignaturaVerification();
  }

  private async initializeSignaturaVerification(): Promise<void> {
    try {
      this.spinner.show();

      const statusResponse = await this.biometricService.checkBiometricStatus().toPromise();
      const status = statusResponse?.data;

      if (status?.is_completed) {
        this.verificationStatus = 'completed';
        this.spinner.hide();
        this.navigateToNextStep();
        return;
      }

      if (status?.needs_verification) {
        await this.startSignaturaProcess();
      }

      this.spinner.hide();
    } catch (error) {
      console.error('Signatura initialization error:', error);
      this.verificationStatus = 'failed';
      this.spinner.hide();
      alert('No se pudo inicializar la verificación de firma. Por favor, intenta nuevamente.');
    }
  }

  private async startSignaturaProcess() {
    try {
      this.spinner.show();

      const verificationCode = this.authService.getVerificationCode();

      if (!verificationCode) {
        this.verificationStatus = 'failed';
        this.spinner.hide();
        alert('Código de verificación no encontrado');
        return;
      }

      const signaturaResponse = await this.biometricService
        .sendSignatureDocument(verificationCode)
        .toPromise();

      if (!signaturaResponse || signaturaResponse.status !== 'ok') {
        throw new Error('No se recibió respuesta válida del servicio de firma');
      }

      this.verificationStatus = 'in_progress';
      this.spinner.hide();

      this.startVerificationPolling();
    } catch (error) {
      console.error('Signatura process start error:', error);
      this.verificationStatus = 'failed';
      this.spinner.hide();
      alert('No se pudo iniciar el proceso de firma. Por favor, intenta nuevamente.');
    }
  }

  private startVerificationPolling() {
    this.biometricService
      .pollBiometricStatus(240, 5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (statusResponse) => {
          const status = statusResponse?.data;

          if (status) {
            this.verificationStatus = status.biometric_status as any;

            if (status.is_completed) {
              this.onVerificationCompleted();
            }
          }
        },
        error: (error) => {
          console.error('Polling error:', error);
          this.verificationStatus = 'failed';
          alert('Se perdió la conexión con el servicio de verificación. Por favor, intenta nuevamente.');
        },
        complete: () => {
          if (this.verificationStatus === 'in_progress') {
            this.verificationStatus = 'failed';
            alert('El tiempo para completar la verificación de firma ha expirado (20 minutos). Puede intentar nuevamente.');
          }
        },
      });
  }

  private onVerificationCompleted() {
    this.verificationStatus = 'completed';
    setTimeout(() => {
      this.navigateToNextStep();
    }, 2000);
  }

  private navigateToNextStep(): void {
    this.router.navigate(['/summary']);
  }

  retryVerification(): void {
    this.verificationStatus = 'pending';
    this.initializeSignaturaVerification();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
